// League detail — owner header + full member ranking (the same numbers
// members see, served by ranking-service's un-gated admin board). Read-only.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchLeagueAdmin, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { Avatar } from "@/components/ui/Avatar";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { LeaderboardTable, type RankMetric } from "@/components/leagues/LeaderboardTable";
import { usePagination } from "@/lib/usePagination";
import { fmtDate } from "@/lib/format";

export const Route = createFileRoute("/_app/leagues/$id")({
  component: LeagueDetailPage,
});

function LeagueDetailPage() {
  const { id } = Route.useParams();
  const [metric, setMetric] = useState<RankMetric>("profit");
  const { page, setPage, limit, offset } = usePagination();
  useEffect(() => {
    setPage(0);
  }, [metric]); // eslint-disable-line react-hooks/exhaustive-deps

  const detail = useQuery({
    queryKey: keys.leagueDetail(id, metric, limit, offset),
    queryFn: () => fetchLeagueAdmin(id, { metric, limit, offset }),
  });

  const lg = detail.data?.league;
  const board = detail.data?.leaderboard;

  return (
    <div className="space-y-6">
      <Link
        to="/leagues"
        className="inline-flex items-center gap-1 text-xs text-muted transition-colors hover:text-fg"
      >
        ← Back to leagues
      </Link>

      {detail.isLoading && <Skeleton className="h-32" />}
      {detail.isError && (
        <p className="rounded bg-danger/10 px-3 py-2 text-sm text-danger">
          {(detail.error as Error).message}
        </p>
      )}

      {lg && (
        <Card>
          <CardBody className="flex flex-wrap items-center gap-4 pt-5">
            <Avatar url={lg.image_url} name={lg.name} size={64} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl tracking-tight">{lg.name}</h1>
                <Badge tone={lg.status === "ACTIVE" ? "success" : lg.status === "DELETED" ? "danger" : "muted"}>
                  {lg.status}
                </Badge>
              </div>
              <p className="mt-1 text-2xs text-muted">
                Owner <span className="text-fg">{lg.owner_name || lg.owner_id}</span> · {lg.member_count} members
                {lg.join_code && (
                  <>
                    {" · code "}
                    <span className="font-mono text-t-2">{lg.join_code}</span>
                  </>
                )}
              </p>
              <p className="mt-0.5 text-2xs text-muted">
                Created {fmtDate(lg.created_at)} · Ends {fmtDate(lg.end_at)}
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Ranking</CardTitle>
          <SegmentedControl<RankMetric>
            options={[
              { value: "profit", label: "Profit" },
              { value: "volume", label: "Volume" },
            ]}
            value={metric}
            onChange={setMetric}
          />
        </CardHeader>
        <CardBody className="p-0">
          {detail.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {board && board.items.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">No members yet.</p>
          )}
          {board && board.items.length > 0 && (
            <LeaderboardTable rows={board.items} metric={metric} ownerID={lg?.owner_id} />
          )}
          {board && (
            <Pagination
              page={board.page}
              currentPage={page}
              onPageChange={setPage}
              itemLabel="members"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
